import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    LinearProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Popover,
    Select,
    Stack,
    SvgIcon,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DateTime } from 'luxon';
import {
    useEffect,
    useId,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type FocusEvent,
    type KeyboardEvent,
} from 'react';
import { useTable, type Column, type Header, type ReactTable, type RowData } from '@tanstack/react-table';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import {
    appFilterOperators,
    applyAppFilters,
    createEmptyAppFilter,
    getAppLookupOptions,
    isAppFilterable,
} from './AppTable.filters';
import { appTableFeatures, createAppColumnDefinitions, formatAppScalar, getInitialTableState } from './AppTable.model';
import { appGrandTotals, buildAppGroupTree, flattenAppGroupTree } from './AppTable.grouping';
import { appEditRowKey, setAppColumnValue, type AppDraftState, type AppSavedRow } from './AppTable.editing';
import { DefaultAppEditor } from './AppTable.editors';
import { AppTableToolbar } from './AppTable.toolbar';
import type {
    AppColumn,
    AppScalarValue,
    AppExportMetadata,
    AppExportContext,
    AppExportRow,
    AppFilterDraft,
    AppFilterModel,
    AppFilterOperator,
    AppFilterState,
    AppTableProps,
} from './AppTable.types';

const operatorLabels: Record<string, string> = {
    contains: 'contains',
    equals: 'equals',
    startsWith: 'starts with',
    isEmpty: 'is empty',
    notEquals: 'does not equal',
    greaterThan: 'greater than',
    atLeast: 'at least',
    lessThan: 'less than',
    atMost: 'at most',
    on: 'on',
    before: 'before',
    onOrBefore: 'on or before',
    after: 'after',
    onOrAfter: 'on or after',
};
const FILTER_ROW_WIDTH = 640;

function filterFieldSx(width: number) {
    return {
        width,
        minWidth: width,
        maxWidth: width,
        flex: `0 0 ${width}px`,
        boxSizing: 'border-box',
        '& .MuiInputBase-root': {
            width: '100%',
            minWidth: 0,
        },
        '& .MuiSelect-select': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
        },
    };
}

const EMPTY_EXPORT_METADATA: AppExportMetadata = {};

function sameFilterValue(left: AppScalarValue | readonly AppScalarValue[], right: AppScalarValue | readonly AppScalarValue[]): boolean {
    if (Array.isArray(left) && Array.isArray(right)) {
        return left.length === right.length && left.every((value, index) => sameFilterValue(value, right[index]));
    }

    if (left instanceof Date && right instanceof Date) {
        return Object.is(left.getTime(), right.getTime());
    }

    return Object.is(left, right);
}

function sameAppFilterModel(left: AppFilterModel, right: AppFilterModel): boolean {
    if (left.logic !== right.logic || left.filters.length !== right.filters.length) {
        return false;
    }

    return left.filters.every((filter, index) => {
        const candidate = right.filters[index];

        if (
            filter.columnId !== candidate.columnId ||
            filter.scalarType !== candidate.scalarType ||
            filter.operator !== candidate.operator ||
            Boolean(filter.negated) !== Boolean(candidate.negated) ||
            (filter.scalarType === 'draft' ? filter.targetScalarType : undefined) !==
                (candidate.scalarType === 'draft' ? candidate.targetScalarType : undefined)
        ) {
            return false;
        }

        const hasValue = Object.prototype.hasOwnProperty.call(filter, 'value');

        if (hasValue !== Object.prototype.hasOwnProperty.call(candidate, 'value')) {
            return false;
        }

        if (!hasValue) {
            return true;
        }

        const leftValue = 'value' in filter ? filter.value : undefined;
        const rightValue = 'value' in candidate ? candidate.value : undefined;

        return sameFilterValue(leftValue, rightValue);
    });
}

type TableIconName =
    | 'columns'
    | 'filter'
    | 'group'
    | 'up'
    | 'down'
    | 'left'
    | 'right'
    | 'close'
    | 'expandLess'
    | 'expandMore'
    | 'chevronRight'
    | 'chevronDown'
    | 'sortAsc'
    | 'sortDesc'
    | 'sortClear'
    | 'hide'
    | 'pinLeft'
    | 'pinRight'
    | 'unpin';

function TableIcon({ name }: { name: TableIconName }) {
    const paths: Record<TableIconName, string> = {
        columns: 'M3 3h18v18H3V3zm2 2v14h4V5H5zm6 0v14h8V5h-8z',
        filter: 'M3 4h18v2H3V4zm3 7h12v2H6v-2zm4 7h4v2h-4v-2z',
        group: 'M3 4h18v4H3V4zm2 2v0h14V6H5zm-2 6h8v4H3v-4zm2 2v0h4v0H5zm10-2h6v4h-6v-4zm2 2v0h2v0h-2zM3 18h18v2H3v-2z',
        up: 'M7.41 14.59 12 10l4.59 4.59L18 13.17l-6-6-6 6z',
        down: 'm7.41 9.41 4.59 4.58 4.59-4.58L18 10.83l-6 6-6-6z',
        left: 'M15.41 7.41 14 6l-6 6 6 6 1.41-1.41L10.83 12z',
        right: 'm8.59 16.59 1.41 1.41 6-6-6-6-1.41 1.41L13.17 12z',
        close: 'M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z',
        expandLess: 'm7.41 15.41 4.59-4.58 4.59 4.58L18 14l-6-6-6 6z',
        expandMore: 'm7.41 8.59 4.59 4.58 4.59-4.58L18 10l-6 6-6-6z',
        chevronRight: 'M9.29 6.71 13.88 12l-4.59 5.29L10.71 19l6-7-6-7z',
        chevronDown: 'M6.71 9.29 12 13.88l5.29-4.59L19 10.71l-7 6-7-6z',
        sortAsc: 'M3 18h6v-2H3v2zm0-5h12v-2H3v2zm0-7v2h18V6H3zm14 14 4-4h-3V4h-2v12h-3l4 4z',
        sortDesc: 'M3 18h18v-2H3v2zm0-5h12v-2H3v2zm0-7v2h6V6H3zm14 14 4-4h-3V4h-2v12h-3l4 4z',
        sortClear:
            'M3 5h18v2H3V5zm0 6h12v2H3v-2zm0 6h8v2H3v-2zm15-1 4 4-1.4 1.4-4-4L12.6 21 11.2 19.6l4-4-4-4 1.4-1.4 4 4 4-4 1.4 1.4-4 4z',
        hide: 'M12 5c-5 0-9.3 3.1-11 7 1.7 3.9 6 7 11 7s9.3-3.1 11-7c-1.7-3.9-6-7-11-7zm0 12a5 5 0 1 1 0-10 5 5 0 0 1 0 10zm0-2a3 3 0 1 0 0-6 3 3 0 0 0 0 6z',
        pinLeft: 'M16 9V4H8v5l-2 2v2h5v7l1 1 1-1v-7h5v-2l-2-2zM10 6h4v4l1 1H9l1-1V6z',
        pinRight: 'M8 9V4h8v5l2 2v2h-5v7l-1 1-1-1v-7H6v-2l2-2zm6-3h-4v4l-1 1h6l-1-1V6z',
        unpin: 'M17 4v2h-2.5l-2 2H17v2l2 2v2h-5v5l-1 1-1-1v-5H7v-2l2-2V8L5 4l1.4-1.4L20 16.2 18.6 18l-4-4H9l-2 2H5v-2l2-2V8L3 4l1.4-1.4L17 15.2V4z',
    };

    return (
        <SvgIcon fontSize="small">
            <path d={paths[name]} />
        </SvgIcon>
    );
}

function formatFilterDate(value: Date, dateTime: boolean): string {
    const date = DateTime.fromJSDate(value);

    return date.isValid ? date.toFormat(dateTime ? "yyyy-MM-dd'T'HH:mm" : 'yyyy-MM-dd') : '';
}

function createFilterForOperator<TRow>(
    column: AppColumn<TRow>,
    operator: string,
    current?: AppFilterState,
    rows: readonly TRow[] = [],
): AppFilterState {
    if (operator === '') return createEmptyAppFilter(column);

    if (operator === 'isEmpty') {
        switch (column.scalarType) {
            case 'text':
                return {
                    columnId: column.id,
                    scalarType: 'text',
                    operator: 'isEmpty',
                };
            case 'number':
                return {
                    columnId: column.id,
                    scalarType: 'number',
                    operator: 'isEmpty',
                };
            case 'date':
                return {
                    columnId: column.id,
                    scalarType: 'date',
                    operator: 'isEmpty',
                };
            case 'dateTime':
                return {
                    columnId: column.id,
                    scalarType: 'dateTime',
                    operator: 'isEmpty',
                };
            case 'lookup':
                return {
                    columnId: column.id,
                    scalarType: 'lookup',
                    operator: 'isEmpty',
                };
            case 'boolean':
                break;
        }
    }

    const typedOperator = operator as AppFilterOperator;
    const currentScalarType = current?.scalarType === 'draft' ? current.targetScalarType : current?.scalarType;
    const hasCurrentValue = current !== undefined && Object.prototype.hasOwnProperty.call(current, 'value');
    const currentValue = current && 'value' in current ? current.value : undefined;

    if (currentScalarType === column.scalarType && hasCurrentValue) {
        return createFilterStateFromValue(column, typedOperator, currentValue, rows);
    }

    return createFilterDraft(column, typedOperator);
}

function createFilterDraft<TRow>(
    column: AppColumn<TRow>,
    operator: '' | AppFilterOperator,
    value?: AppScalarValue | readonly AppScalarValue[],
    hasValue = false,
): AppFilterDraft {
    const draft: AppFilterDraft = {
        ...createEmptyAppFilter(column),
        operator,
    };

    return hasValue ? { ...draft, value } : draft;
}

function createFilterStateFromValue<TRow>(
    column: AppColumn<TRow>,
    operator: AppFilterOperator,
    value: AppScalarValue | readonly AppScalarValue[],
    rows: readonly TRow[] = [],
): AppFilterState {
    const draft = createFilterDraft(column, operator);

    switch (column.scalarType) {
        case 'text':
            return typeof value === 'string' && value.length > 0
                ? { columnId: column.id, scalarType: 'text', operator: operator as 'contains' | 'equals' | 'startsWith', value }
                : draft;
        case 'number':
            if (typeof value === 'number' && Number.isFinite(value)) {
                return { columnId: column.id, scalarType: 'number', operator: operator as 'equals' | 'notEquals' | 'greaterThan' | 'atLeast' | 'lessThan' | 'atMost', value };
            }
            if (typeof value === 'string' && value.trim() !== '') {
                const parsed = Number(value);
                return Number.isFinite(parsed)
                    ? { columnId: column.id, scalarType: 'number', operator: operator as 'equals' | 'notEquals' | 'greaterThan' | 'atLeast' | 'lessThan' | 'atMost', value: parsed }
                    : createFilterDraft(column, operator, value, true);
            }
            return draft;
        case 'date':
        case 'dateTime': {
            let parsed: Date | undefined;
            if (value instanceof Date) parsed = Number.isNaN(value.getTime()) ? undefined : value;
            else if (typeof value === 'string' && value !== '') {
                const candidate = column.scalarType === 'date' ? new Date(`${value}T00:00`) : new Date(value);
                parsed = Number.isNaN(candidate.getTime()) ? undefined : candidate;
            }
            if (parsed) {
                return { columnId: column.id, scalarType: column.scalarType, operator: operator as 'on' | 'before' | 'onOrBefore' | 'after' | 'onOrAfter', value: parsed };
            }
            return typeof value === 'string' && value !== '' ? createFilterDraft(column, operator, value, true) : draft;
        }
        case 'boolean':
            return typeof value === 'boolean' ? { columnId: column.id, scalarType: 'boolean', operator: 'equals', value } : draft;
        case 'lookup':
            if (operator === 'in') {
                const values = Array.isArray(value) ? value : [value];

                return values.length > 0
                    ? {
                        columnId: column.id,
                        scalarType: 'lookup',
                        operator: 'in',
                        value: values,
                    }
                    : draft;
            }

            if (Array.isArray(value)) {
                return createFilterStateFromValue(column, operator, value[0], rows);
            }

            return getAppLookupOptions(column, rows).some((option) => Object.is(option.value, value))
                ? { columnId: column.id, scalarType: 'lookup', operator: 'equals', value: value as AppScalarValue }
                : draft;
    }
}

function createFilterStateFromInput<TRow>(
    column: AppColumn<TRow>,
    operator: AppFilterOperator,
    rawValue: string,
    rows: readonly TRow[] = [],
): AppFilterState {
    if (rawValue === '') return createFilterDraft(column, operator);
    if (column.scalarType === 'number') {
        const parsed = Number(rawValue);
        return Number.isFinite(parsed)
            ? createFilterStateFromValue(column, operator, parsed, rows)
            : createFilterDraft(column, operator, rawValue, true);
    }
    return createFilterStateFromValue(column, operator, rawValue, rows);
}

function isCurrentFilterTarget<TRow>(
    filter: AppFilterState,
    column: AppColumn<TRow>,
    operator: AppFilterOperator,
): boolean {
    const scalarType = filter.scalarType === 'draft' ? filter.targetScalarType : filter.scalarType;

    return filter.columnId === column.id && scalarType === column.scalarType && filter.operator === operator;
}

function pinningStyles<TRow extends RowData>(
    column: Column<typeof appTableFeatures, TRow, unknown>,
): React.CSSProperties {
    const pinned = column.getIsPinned();

    return pinned === 'start'
        ? {
              position: 'sticky',
              left: column.getStart('start'),
              zIndex: 2,
          }
        : pinned === 'end'
          ? {
                position: 'sticky',
                right: column.getAfter('end'),
                zIndex: 2,
            }
          : {};
}

function appRowId<TRow>(getRowId: (row: TRow) => string | number) {
    return (row: TRow): string => {
        const id = getRowId(row);

        return `${typeof id}:${String(id)}`;
    };
}

function defaultAppCell<TRow>(column: AppColumn<TRow>, row: TRow) {
    const value = column.accessor(row);
    const lookupLabel =
        column.scalarType === 'lookup'
            ? (column.lookup?.labelFor?.(value, row) ??
              column.lookup?.options?.find((option) => Object.is(option.value, value))?.label)
            : undefined;
    const formatted = column.formatter
        ? column.formatter(value, row)
        : (lookupLabel ?? formatAppScalar(value, column.scalarType));

    return column.cell
        ? column.cell({
              row,
              value,
              formatted,
          })
        : formatted;
}

function AppFilterControls<TRow>({
    columns,
    rows,
    filters,
    logic,
    updateFilters,
    onLogicChange,
}: {
    columns: readonly AppColumn<TRow>[];
    rows: readonly TRow[];
    filters: readonly AppFilterState[];
    logic: 'and' | 'or';
    updateFilters: (update: (filters: readonly AppFilterState[]) => readonly AppFilterState[]) => void;
    onLogicChange: (logic: 'and' | 'or') => void;
}) {
    const idPrefix = useId();
    const filterableColumns = columns.filter(isAppFilterable);
    const updateFilter = (index: number, update: (filter: AppFilterState) => AppFilterState) => {
        updateFilters((current) =>
            current.map((filter, filterIndex) => {
                if (filterIndex !== index) {
                    return filter;
                }

                const next = update(filter);

                return next.negated === undefined && filter.negated
                    ? { ...next, negated: true } : next;
            }),
        );
    };
    const addFilter = () => {
        updateFilters((current) => [...current, createEmptyAppFilter()]);
    };

    return (
        <Stack
            spacing={1.5}
            sx={{
                p: 1.5,
            }}
        >
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <FormControl
                    size="small"
                    variant="standard"
                    sx={{
                        minWidth: 120,
                    }}
                >
                    <InputLabel id={`${idPrefix}-filter-logic-label`}>Filter logic</InputLabel>
                    <Select
                        native
                        variant="standard"
                        labelId={`${idPrefix}-filter-logic-label`}
                        label="Filter logic"
                        inputProps={{
                            'aria-label': 'Filter logic',
                        }}
                        value={logic}
                        onChange={(event) => onLogicChange(event.target.value as 'and' | 'or')}
                    >
                        <option value="and">AND</option>
                        <option value="or">OR</option>
                    </Select>
                </FormControl>
            </Stack>
            <Stack spacing={1} sx={{ width: '100%', overflowX: 'auto' }}>
                {filters.map((filter, index) => {
                    const selectedColumn = filterableColumns.find((column) => column.id === filter.columnId);
                    const filterScalarType = filter.scalarType === 'draft' ? filter.targetScalarType : filter.scalarType;
                    const filterMatchesColumn = selectedColumn !== undefined && filterScalarType === selectedColumn.scalarType;
                    const operators = selectedColumn ? appFilterOperators[selectedColumn.scalarType] : [];
                    const operator = filterMatchesColumn && operators.some((option) => option === filter.operator)
                        ? filter.operator as AppFilterOperator
                        : '';
                    const valueLabel = `Filter value ${index + 1}`;
                    const hasStoredValue = Object.prototype.hasOwnProperty.call(filter, 'value');
                    const storedValue = filter && 'value' in filter ? filter.value : undefined;
                    const lookupOptions = selectedColumn?.scalarType === 'lookup'
                        ? getAppLookupOptions(selectedColumn, rows)
                        : [];
                    const selectedLookupIndex = selectedColumn?.scalarType === 'lookup' && operator === 'equals' && hasStoredValue
                        ? lookupOptions.findIndex((option) => Object.is(option.value, storedValue))
                        : -1;
                    const hasValue = selectedColumn !== undefined && operator !== '' && operator !== 'isEmpty';

                    return (
                        <Stack
                            key={`${filter.columnId}-${index}`}
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            useFlexGap
                            sx={{
                                width: FILTER_ROW_WIDTH,
                                minWidth: FILTER_ROW_WIDTH,
                                boxSizing: 'border-box',
                                pr: 3,
                                flexWrap: 'nowrap',
                            }}
                        >
                            <FormControlLabel
                                label="Not"
                                labelPlacement="top"
                                sx={{
                                    m: 0,
                                    width: 40,
                                    flexShrink: 0,
                                    alignSelf: 'stretch',
                                    justifyContent: 'flex-end',
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: 12,
                                        lineHeight: '16px',
                                        color: 'text.secondary',
                                    },
                                }}
                                control={<Checkbox
                                    size="small"
                                    sx={{
                                        p: 0,
                                        width: 32,
                                        height: 32,
                                    }}
                                    checked={filter.negated ?? false}
                                    inputProps={{
                                        'aria-label': `Not filter ${index + 1}`,
                                    }}
                                    onChange={(_, checked) => updateFilter(index, current => ({
                                        ...current,
                                        negated: checked,
                                    }))}
                                />}
                            />
                            <FormControl
                                size="small"
                                variant="standard"
                                sx={filterFieldSx(130)}
                            >
                                <InputLabel shrink id={`${idPrefix}-filter-column-${index}-label`}>Column</InputLabel>
                                <Select
                                    native
                                    variant="standard"
                                    labelId={`${idPrefix}-filter-column-${index}-label`}
                                    label="Column"
                                    inputProps={{
                                        'aria-label': `Filter column ${index + 1}`,
                                    }}
                                    value={selectedColumn ? selectedColumn.id : ''}
                                    onChange={(event) => {
                                        const nextColumn = filterableColumns.find(
                                            (column) => column.id === event.target.value,
                                        );

                                        if (nextColumn) {
                                            updateFilter(index, () => createEmptyAppFilter(nextColumn));
                                        }
                                    }}
                                >
                                    <option disabled value="">Select a column</option>
                                    {filterableColumns.map((column) => (
                                        <option key={column.id} value={column.id}>
                                            {column.label}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl
                                size="small"
                                variant="standard"
                                sx={filterFieldSx(160)}
                            >
                                <InputLabel shrink id={`${idPrefix}-filter-operator-${index}-label`}>Operator</InputLabel>
                                <Select
                                    native
                                    variant="standard"
                                    labelId={`${idPrefix}-filter-operator-${index}-label`}
                                    label="Operator"
                                    inputProps={{
                                        'aria-label': `Filter operator ${index + 1}`,
                                    }}
                                    value={operator}
                                    disabled={!selectedColumn}
                                    onChange={(event) =>
                                        updateFilter(index, (current) =>
                                                selectedColumn
                                                    ? createFilterForOperator(selectedColumn, event.target.value, current, rows)
                                                    : current,
                                        )
                                    }
                                >
                                    <option disabled value="">Select an operator</option>
                                    {operators.map((operator) => (
                                        <option key={operator} value={operator}>
                                            {operatorLabels[operator] ?? operator}
                                        </option>
                                    ))}
                                </Select>
                            </FormControl>
                            {hasValue && selectedColumn?.scalarType === 'text' && (
                                <TextField
                                    variant="standard"
                                    size="small"
                                    label="Value"
                                    sx={filterFieldSx(220)}
                                    inputProps={{
                                        'aria-label': valueLabel,
                                    }}
                                    value={typeof storedValue === 'string' ? storedValue : ''}
                                    onChange={(event) =>
                                        updateFilter(index, (current) =>
                                            isCurrentFilterTarget(current, selectedColumn, operator)
                                                ? createFilterStateFromInput(selectedColumn, operator, event.target.value, rows)
                                                : current,
                                        )
                                    }
                                />
                            )}
                            {hasValue && selectedColumn?.scalarType === 'number' && (
                                <TextField
                                    size="small"
                                    variant="standard"
                                    type="number"
                                    label="Value"
                                    sx={filterFieldSx(220)}
                                    inputProps={{
                                        'aria-label': valueLabel,
                                    }}
                                    value={typeof storedValue === 'number' || typeof storedValue === 'string' ? storedValue : ''}
                                    onChange={(event) => {
                                        const value = (event.target as HTMLInputElement).value;
                                        updateFilter(index, (current) =>
                                            isCurrentFilterTarget(current, selectedColumn, operator)
                                                ? createFilterStateFromInput(selectedColumn, operator, value, rows)
                                                : current,
                                        );
                                    }}
                                />
                            )}
                            {hasValue && (selectedColumn?.scalarType === 'date' || selectedColumn?.scalarType === 'dateTime') && (
                                <TextField
                                    size="small"
                                    variant="standard"
                                    type={selectedColumn.scalarType === 'date' ? 'date' : 'datetime-local'}
                                    label="Value"
                                    sx={filterFieldSx(220)}
                                    inputProps={{
                                        'aria-label': valueLabel,
                                    }}
                                    value={storedValue instanceof Date ? formatFilterDate(storedValue, selectedColumn.scalarType === 'dateTime') : typeof storedValue === 'string' ? storedValue : ''}
                                    onChange={(event) => {
                                        const value = event.target.value;
                                        updateFilter(index, (current) =>
                                            isCurrentFilterTarget(current, selectedColumn, operator)
                                                ? createFilterStateFromInput(selectedColumn, operator, value, rows)
                                                : current,
                                        );
                                    }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            )}
                            {hasValue && selectedColumn?.scalarType === 'boolean' && (
                                <FormControl
                                    size="small"
                                    variant="standard"
                                    sx={filterFieldSx(220)}
                                >
                                    <InputLabel shrink id={`${idPrefix}-filter-value-${index}-label`}>Value</InputLabel>
                                    <Select
                                        native
                                        variant="standard"
                                        labelId={`${idPrefix}-filter-value-${index}-label`}
                                        label="Value"
                                        inputProps={{
                                            'aria-label': valueLabel,
                                        }}
                                        value={typeof storedValue === 'boolean' ? String(storedValue) : ''}
                                        onChange={(event) =>
                                            updateFilter(index, (current) =>
                                                isCurrentFilterTarget(current, selectedColumn, operator)
                                                    ? event.target.value === ''
                                                        ? createFilterDraft(selectedColumn, operator)
                                                        : createFilterStateFromValue(selectedColumn, operator, event.target.value === 'true', rows)
                                                    : current,
                                            )
                                        }
                                    >
                                        <option value="">Select a value</option>
                                        <option value="true">True</option>
                                        <option value="false">False</option>
                                    </Select>
                                </FormControl>
                            )}
                            {hasValue && selectedColumn?.scalarType === 'lookup' && operator === 'in' && (
                                <FormControl size="small" variant="standard" sx={filterFieldSx(220)}>
                                    <InputLabel shrink id={`${idPrefix}-filter-value-${index}-label`}>Values</InputLabel>
                                    <Select
                                        multiple
                                        variant="standard"
                                        labelId={`${idPrefix}-filter-value-${index}-label`}
                                        SelectDisplayProps={{
                                            'aria-label': valueLabel,
                                            'aria-labelledby': undefined,
                                        }}
                                        value={lookupOptions.flatMap((option, optionIndex) =>
                                            Array.isArray(storedValue) && storedValue.some(value => Object.is(value, option.value))
                                                ? [String(optionIndex)] : [],
                                        )}
                                        renderValue={selected => (selected as string[])
                                            .map(index => lookupOptions[Number(index)].label).join(', ')}
                                        onChange={event => {
                                            const indices = typeof event.target.value === 'string'
                                                ? event.target.value.split(',') : event.target.value;
                                            const values = indices.map(index => lookupOptions[Number(index)].value);

                                            updateFilter(index, current =>
                                                isCurrentFilterTarget(current, selectedColumn, operator)
                                                    ? createFilterStateFromValue(selectedColumn, operator, values, rows)
                                                    : current,
                                            );
                                        }}
                                    >
                                        {lookupOptions.map((option, optionIndex) => (
                                            <MenuItem key={optionIndex} value={String(optionIndex)}>
                                                <Checkbox checked={Array.isArray(storedValue)
                                                    && storedValue.some(value => Object.is(value, option.value))} />
                                                <ListItemText primary={option.label} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {hasValue && selectedColumn?.scalarType === 'lookup' && operator === 'equals' && (
                                <FormControl
                                    size="small"
                                    variant="standard"
                                    sx={filterFieldSx(220)}
                                >
                                    <InputLabel shrink id={`${idPrefix}-filter-value-${index}-label`}>Value</InputLabel>
                                    <Select
                                        native
                                        variant="standard"
                                        labelId={`${idPrefix}-filter-value-${index}-label`}
                                        label="Value"
                                        inputProps={{
                                            'aria-label': valueLabel,
                                        }}
                                        value={selectedLookupIndex < 0 ? '-1' : String(selectedLookupIndex)}
                                        onChange={(event) => {
                                            const option = lookupOptions[Number(event.target.value)];

                                            if (event.target.value === '-1') {
                                                updateFilter(index, (current) =>
                                                    isCurrentFilterTarget(current, selectedColumn, operator)
                                                        ? createFilterDraft(selectedColumn, operator)
                                                        : current,
                                                );
                                            } else if (option) {
                                                updateFilter(index, (current) =>
                                                    isCurrentFilterTarget(current, selectedColumn, operator)
                                                        ? createFilterStateFromValue(selectedColumn, operator, option.value, rows)
                                                        : current,
                                                );
                                            }
                                        }}
                                    >
                                        <option value="-1">Select a value</option>
                                        {lookupOptions.map((option, optionIndex) => (
                                            <option key={`${optionIndex}`} value={optionIndex}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {operator !== 'isEmpty' && !hasValue && (
                                <TextField
                                    variant="standard"
                                    size="small"
                                    label="Value"
                                    disabled
                                    sx={filterFieldSx(220)}
                                    inputProps={{ 'aria-label': valueLabel }}
                                />
                            )}
                            {selectedColumn && operator === 'isEmpty' && <Box aria-hidden="true" sx={filterFieldSx(220)} />}
                            <IconButton
                                aria-label={`Remove filter ${index + 1}`}
                                size="small"
                                sx={{
                                    width: 32,
                                    minWidth: 32,
                                    maxWidth: 32,
                                    height: 32,
                                    flex: '0 0 32px',
                                }}
                                onClick={() =>
                                    updateFilters((current) =>
                                        current.filter((_item, filterIndex) => filterIndex !== index),
                                    )
                                }
                            >
                                <TableIcon name="close" />
                            </IconButton>
                        </Stack>
                    );
                })}
            </Stack>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Button size="small" onClick={addFilter} disabled={filterableColumns.length === 0}>
                    Add filter
                </Button>
                <Button size="small" onClick={() => updateFilters(() => [])} disabled={filters.length === 0}>
                    Clear filters
                </Button>
            </Stack>
        </Stack>
    );
}

function AppColumnMenu<TRow extends RowData>({
    table,
    columnId,
    anchorEl,
    appColumn,
    autoHideMenu,
    onAutoHideMenuChange,
    onFilter,
    groupingAllowed,
    isGrouped,
    onGrouping,
    allowVisibility,
    onManageColumns,
    onClose,
}: {
    table: ReactTable<typeof appTableFeatures, TRow>;
    columnId: string;
    anchorEl: HTMLElement;
    appColumn: AppColumn<TRow>;
    autoHideMenu: boolean;
    onAutoHideMenuChange: (enabled: boolean) => void;
    onFilter?: () => void;
    groupingAllowed: boolean;
    isGrouped: boolean;
    onGrouping: () => void;
    allowVisibility: boolean;
    onManageColumns?: () => void;
    onClose: () => void;
}) {
    const column = table.getColumn(columnId);

    if (!column) {
        return null;
    }

    const columns = table.getAllLeafColumns();
    const index = columns.findIndex((item) => item.id === columnId);
    const move = (direction: -1 | 1) => {
        const next = [...columns.map((item) => item.id)];
        const target = index + direction;

        if (target < 0 || target >= next.length) {
            return;
        }
        [next[index], next[target]] = [next[target], next[index]];
        table.setColumnOrder(next);
        onClose();
    };

    return (
        <Menu anchorEl={anchorEl} open onClose={onClose}>
            {column.getCanSort() && (
                <>
                    <MenuItem
                        selected={column.getIsSorted() === 'asc'}
                        onClick={() => {
                            column.toggleSorting(false);
                            onClose();
                        }}
                    >
                        <ListItemIcon>
                            <TableIcon name="sortAsc" />
                        </ListItemIcon>
                        Sort ascending
                    </MenuItem>
                    <MenuItem
                        selected={column.getIsSorted() === 'desc'}
                        onClick={() => {
                            column.toggleSorting(true);
                            onClose();
                        }}
                    >
                        <ListItemIcon>
                            <TableIcon name="sortDesc" />
                        </ListItemIcon>
                        Sort descending
                    </MenuItem>
                    <MenuItem
                        disabled={!column.getIsSorted()}
                        onClick={() => {
                            column.clearSorting();
                            onClose();
                        }}
                    >
                        <ListItemIcon>
                            <TableIcon name="sortClear" />
                        </ListItemIcon>
                        Clear sort
                    </MenuItem>
                    <Divider />
                </>
            )}
            {onFilter && isAppFilterable(appColumn) && (
                <MenuItem
                    onClick={() => {
                        onFilter();
                        onClose();
                    }}
                >
                    <ListItemIcon>
                        <TableIcon name="filter" />
                    </ListItemIcon>
                    Filter
                </MenuItem>
            )}
            {groupingAllowed && appColumn.kind !== 'action' && appColumn.groupable !== false && (
                <MenuItem
                    onClick={() => {
                        onGrouping();
                        onClose();
                    }}
                >
                    <ListItemIcon>
                        <TableIcon name="group" />
                    </ListItemIcon>
                    {isGrouped ? 'Remove grouping' : 'Group by this column'}
                </MenuItem>
            )}
            {((onFilter && isAppFilterable(appColumn)) ||
                (groupingAllowed && appColumn.kind !== 'action' && appColumn.groupable !== false)) && <Divider />}
            {allowVisibility && column.getCanHide() && (
                <MenuItem
                    onClick={() => {
                        column.toggleVisibility(false);
                        onClose();
                    }}
                >
                    <ListItemIcon>
                        <TableIcon name="hide" />
                    </ListItemIcon>
                    Hide column
                </MenuItem>
            )}
            {onManageColumns && (
                <MenuItem
                    onClick={() => {
                        onManageColumns();
                        onClose();
                    }}
                >
                    <ListItemIcon>
                        <TableIcon name="columns" />
                    </ListItemIcon>
                    Manage columns
                </MenuItem>
            )}
            {((allowVisibility && column.getCanHide()) || onManageColumns) && <Divider />}
            <MenuItem disabled={index === 0} onClick={() => move(-1)}>
                <ListItemIcon>
                    <TableIcon name="left" />
                </ListItemIcon>
                Move left
            </MenuItem>
            <MenuItem disabled={index === columns.length - 1} onClick={() => move(1)}>
                <ListItemIcon>
                    <TableIcon name="right" />
                </ListItemIcon>
                Move right
            </MenuItem>
            {column.getCanPin() && (
                <MenuItem
                    selected={column.getIsPinned() === 'start'}
                    disabled={column.getIsPinned() === 'start'}
                    onClick={() => {
                        column.pin('start');
                        onClose();
                    }}
                >
                    <ListItemIcon>
                        <TableIcon name="pinLeft" />
                    </ListItemIcon>
                    Pin left
                </MenuItem>
            )}
            {column.getCanPin() && (
                <MenuItem
                    selected={column.getIsPinned() === 'end'}
                    disabled={column.getIsPinned() === 'end'}
                    onClick={() => {
                        column.pin('end');
                        onClose();
                    }}
                >
                    <ListItemIcon>
                        <TableIcon name="pinRight" />
                    </ListItemIcon>
                    Pin right
                </MenuItem>
            )}
            {column.getIsPinned() && (
                <MenuItem
                    onClick={() => {
                        column.pin(false);
                        onClose();
                    }}
                >
                    <ListItemIcon>
                        <TableIcon name="unpin" />
                    </ListItemIcon>
                    Unpin
                </MenuItem>
            )}
            <Divider />
            <MenuItem dense onClick={() => onAutoHideMenuChange(!autoHideMenu)}>
                <Checkbox
                    checked={autoHideMenu}
                    size="small"
                    tabIndex={-1}
                    disableRipple
                    inputProps={{
                        'aria-label': 'Auto-hide column menu buttons',
                    }}
                />
                <ListItemText primary="Auto-hide column menu buttons" />
            </MenuItem>
        </Menu>
    );
}

/** A MUI table renderer backed by TanStack Table's v9 row, sort, order, pin, visibility and pagination models. */
export function AppTable<TRow extends RowData>({
    rows,
    columns,
    getRowId,
    loading = false,
    density = 'standard',
    pagination,
    columnControls,
    filterControls = false,
    filterLogic: legacyInitialFilterLogic = 'and',
    initialFilterModel,
    filterModel,
    onFilterModelChange,
    groupingEnabled,
    initialGrouping,
    export: exportOptions,
    onExportContextChange,
    editing,
}: AppTableProps<TRow>) {
    const exportMetadata = exportOptions === false ? EMPTY_EXPORT_METADATA : (exportOptions ?? EMPTY_EXPORT_METADATA);
    const theme = useTheme();
    const groupIdPrefix = useId();
    const [localFilterModel, setLocalFilterModel] = useState<AppFilterModel>(
        () =>
            initialFilterModel ?? {
                filters: [],
                logic: legacyInitialFilterLogic,
            },
    );
    const isFilterModelControlled = filterModel !== undefined;
    const effectiveFilterModel = filterModel ?? localFilterModel;
    const { filters, logic: filterLogic } = effectiveFilterModel;
    const effectiveFilterModelRef = useRef(effectiveFilterModel);

    effectiveFilterModelRef.current = effectiveFilterModel;

    const previousFilterModelRef = useRef(effectiveFilterModel);
    const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null);
    const [groupingAnchor, setGroupingAnchor] = useState<HTMLElement | null>(null);
    const [columnMenu, setColumnMenu] = useState<{ columnId: string; anchor: HTMLElement } | undefined>();
    const [visibilityMenu, setVisibilityMenu] = useState<HTMLElement | null>(null);
    const [autoHideMenu, setAutoHideMenu] = useState(columnControls?.autoHideMenu ?? true);
    const columnsButtonRef = useRef<HTMLButtonElement | null>(null);
    const filterButtonRef = useRef<HTMLButtonElement | null>(null);
    const groupingAllowed = !editing && (groupingEnabled ?? true);
    const groupableColumns = columns.filter((column) => column.kind !== 'action' && column.groupable !== false);
    const [grouping, setGrouping] = useState<readonly string[]>(initialGrouping ?? []);
    const requestFilterModelChange = (update: (current: AppFilterModel) => AppFilterModel) => {
        const next = update(effectiveFilterModelRef.current);

        if (sameAppFilterModel(effectiveFilterModelRef.current, next)) {
            return;
        }

        if (isFilterModelControlled) {
            onFilterModelChange?.(next);

            return;
        }
        effectiveFilterModelRef.current = next;
        setLocalFilterModel(next);
        onFilterModelChange?.(next);
    };
    const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<string>>(() => new Set());
    const [groupToAdd, setGroupToAdd] = useState('');
    const [drafts, setDrafts] = useState<ReadonlyMap<string, AppDraftState<TRow>>>(() => new Map());
    const [savedRows, setSavedRows] = useState<ReadonlyMap<string, AppSavedRow<TRow>>>(() => new Map());
    const [activeRow, setActiveRow] = useState<{ key: string; columnId: string } | undefined>();
    const [activeCell, setActiveCell] = useState<{ key: string; columnId: string } | undefined>();
    const [savingRows, setSavingRows] = useState<ReadonlySet<string>>(() => new Set());
    const draftsRef = useRef(drafts);

    draftsRef.current = drafts;

    const savedRowsRef = useRef(savedRows);

    savedRowsRef.current = savedRows;

    const editorOpenRef = useRef(false);
    const suppressPopupBlurRef = useRef(false);
    const commitTokensRef = useRef(new Set<string>());
    const commitPromisesRef = useRef(new Map<string, Promise<boolean>>());
    const cellRefs = useRef(new Map<string, HTMLTableCellElement>());
    const resizeEnabled = columnControls?.resize ?? false;
    const tableColumns = useMemo(() => createAppColumnDefinitions(columns, resizeEnabled), [columns, resizeEnabled]);
    const sourceRowsByKey = useMemo(
        () => new Map(rows.map((row) => [appEditRowKey(getRowId(row)), row])),
        [rows, getRowId],
    );
    const tableRows = useMemo(
        () =>
            rows.map((row) => {
                const key = appEditRowKey(getRowId(row));

                return drafts.get(key)?.original ?? savedRows.get(key)?.value ?? row;
            }),
        [rows, getRowId, drafts, savedRows],
    );

    useEffect(() => {
        setSavedRows((current) => {
            const next = new Map(current);

            for (const [key, saved] of next) {
                const refreshed = sourceRowsByKey.get(key);

                if (refreshed && refreshed !== saved.source) {
                    next.delete(key);
                }
            }

            return next.size === current.size ? current : next;
        });
    }, [sourceRowsByKey]);

    const filteredRows = useMemo(
        () => applyAppFilters(tableRows, columns, filters, filterLogic),
        [tableRows, columns, filters, filterLogic],
    );
    const initialState = useMemo(
        () =>
            getInitialTableState({
                pagination,
                columns,
            }),
        [pagination?.pageSize, Boolean(pagination), columns],
    );
    const table = useTable<typeof appTableFeatures, TRow>({
        features: appTableFeatures,
        columns: tableColumns,
        data: filteredRows,
        getRowId: appRowId(getRowId),
        initialState,
        manualPagination: true,
        autoResetPageIndex: false,
    });

    useEffect(() => {
        if (!sameAppFilterModel(previousFilterModelRef.current, effectiveFilterModel)) {
            table.setPageIndex(0);
        }
        previousFilterModelRef.current = effectiveFilterModel;
    }, [effectiveFilterModel, table]);
    useEffect(() => {
        if (!pagination) {
            return;
        }

        const pageSize = pagination.pageSize ?? 100;

        if (table.state.pagination.pageSize !== pageSize) {
            table.setPageSize(pageSize);
        }
    }, [pagination?.pageSize, Boolean(pagination), table]);

    const sortedRowModel = table.getPrePaginatedRowModel();
    const sortedRows = useMemo(() => sortedRowModel.rows.map((row) => row.original), [sortedRowModel]);
    const groupedTree = useMemo(
        () => buildAppGroupTree(sortedRows, columns, groupingAllowed ? grouping : [], getRowId),
        [sortedRows, columns, groupingAllowed, grouping, getRowId],
    );
    const flattenedRows = useMemo(
        () => flattenAppGroupTree(groupedTree, collapsedGroups),
        [groupedTree, collapsedGroups],
    );
    const pageSize = pagination ? table.state.pagination.pageSize : Math.max(flattenedRows.length, 1);
    const pageIndex = pagination ? table.state.pagination.pageIndex : 0;
    const visibleRows = flattenedRows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    useEffect(() => {
        if (!pagination) {
            return;
        }

        const lastValidPage = Math.max(0, Math.ceil(flattenedRows.length / table.state.pagination.pageSize) - 1);

        if (table.state.pagination.pageIndex > lastValidPage) {
            table.setPageIndex(lastValidPage);
        }
    }, [
        Boolean(pagination),
        flattenedRows.length,
        table.state.pagination.pageIndex,
        table.state.pagination.pageSize,
        table,
    ]);

    const appColumnsById = useMemo(() => new Map(columns.map((column) => [column.id, column])), [columns]);
    const grandTotals = useMemo(() => appGrandTotals(sortedRows, columns), [sortedRows, columns]);
    const columnMenuProps = columnMenu
        ? {
              ...columnMenu,
          }
        : undefined;
    const columnMenuAppColumn = columnMenuProps ? appColumnsById.get(columnMenuProps.columnId) : undefined;
    const rowHeight = density === 'compact' ? 36 : density === 'comfortable' ? 56 : 44;
    const containerRef = useRef<HTMLDivElement | null>(null);
    const headerRowRefs = useRef(new Map<number, HTMLTableRowElement>());
    const [headerOffsets, setHeaderOffsets] = useState<readonly number[]>([0]);
    const [headerHeight, setHeaderHeight] = useState(0);
    const [containerWidth, setContainerWidth] = useState(960);
    const autoFlexWidths = useRef<Record<string, number>>({});
    const manualFlexWidths = useRef(new Set<string>());
    const visibleColumns = useMemo(
        () => [
            ...table.getStartVisibleLeafColumns(),
            ...table.getCenterVisibleLeafColumns(),
            ...table.getEndVisibleLeafColumns(),
        ],
        [tableColumns, table.state.columnOrder, table.state.columnPinning, table.state.columnVisibility],
    );
    const startColumns = table.getStartVisibleLeafColumns();
    const centerColumns = table.getCenterVisibleLeafColumns();
    const endColumns = table.getEndVisibleLeafColumns();
    const flexColumns = visibleColumns.filter((column) => {
        const appColumn = appColumnsById.get(column.id);

        return (
            appColumn?.width === undefined && appColumn?.flex !== undefined && !manualFlexWidths.current.has(column.id)
        );
    });
    const flexFixedWidth = visibleColumns.reduce(
        (total, column) => (flexColumns.includes(column) ? total : total + column.getSize()),
        0,
    );
    const flexWeight = flexColumns.reduce((total, column) => total + (appColumnsById.get(column.id)?.flex ?? 1), 0);
    const flexAvailable = Math.max(0, containerWidth - flexFixedWidth);
    const nextFlexWidths = Object.fromEntries(
        flexColumns.map((column) => {
            const appColumn = appColumnsById.get(column.id);
            const weighted = flexWeight > 0 ? (flexAvailable * (appColumn?.flex ?? 1)) / flexWeight : 0;

            return [column.id, Math.max(appColumn?.minWidth ?? 100, weighted)];
        }),
    );

    useLayoutEffect(() => {
        if (!containerRef.current || typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver((entries) => {
            const width = entries[0]?.contentRect.width;

            if (width && width !== containerWidth) {
                setContainerWidth(width);
            }
        });

        observer.observe(containerRef.current);
        setContainerWidth(containerRef.current.clientWidth || 960);

        return () => observer.disconnect();
    }, []);
    useLayoutEffect(() => {
        if (flexColumns.length === 0) {
            return;
        }
        table.setColumnSizing((current) => {
            const next = {
                ...current,
            };
            let changed = false;

            for (const column of flexColumns) {
                const currentSize = current[column.id];
                const previousAuto = autoFlexWidths.current[column.id];

                if (
                    manualFlexWidths.current.has(column.id) ||
                    (currentSize !== undefined && previousAuto !== undefined && currentSize !== previousAuto)
                ) {
                    manualFlexWidths.current.add(column.id);
                    continue;
                }

                const nextSize = nextFlexWidths[column.id] ?? column.getSize();

                if (currentSize !== nextSize) {
                    next[column.id] = nextSize;
                    changed = true;
                }
                autoFlexWidths.current[column.id] = nextSize;
            }

            return changed ? next : current;
        });
    }, [containerWidth, flexColumns, table]);

    const activeEditKey = activeRow?.key ?? activeCell?.key;
    const activeLeafIndex = activeEditKey
        ? visibleRows.findIndex((row) => row.kind === 'leaf' && appEditRowKey(row.rowId) === activeEditKey)
        : -1;
    const rowVirtualizationEnabled = visibleRows.length > 80;
    const rowVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
        count: visibleRows.length,
        getScrollElement: () => containerRef.current,
        estimateSize: () => rowHeight,
        overscan: 8,
        enabled: rowVirtualizationEnabled,
        initialRect: {
            width: 960,
            height: 640,
        },
        scrollMargin: headerHeight,
        rangeExtractor: (range) => {
            const indexes = defaultRangeExtractor(range);

            return activeLeafIndex >= 0 && !indexes.includes(activeLeafIndex)
                ? [...indexes, activeLeafIndex].sort((left, right) => left - right)
                : indexes;
        },
        getItemKey: (index) => {
            const row = visibleRows[index];

            return row?.kind === 'group'
                ? row.id
                : row?.kind === 'leaf'
                  ? `leaf:${typeof row.rowId}:${String(row.rowId)}`
                  : index;
        },
    });
    const columnVirtualizationEnabled = centerColumns.length > 20;
    const columnVirtualizer = useVirtualizer<HTMLDivElement, HTMLTableCellElement>({
        count: centerColumns.length,
        getScrollElement: () => containerRef.current,
        horizontal: true,
        estimateSize: (index) => centerColumns[index]?.getSize() ?? 150,
        overscan: 3,
        enabled: columnVirtualizationEnabled,
        initialRect: {
            width: 960,
            height: 640,
        },
        getItemKey: (index) => centerColumns[index]?.id ?? index,
    });
    const renderedCenterIndexes = new Set(
        columnVirtualizationEnabled
            ? columnVirtualizer.getVirtualItems().map((item) => item.index)
            : centerColumns.map((_column, index) => index),
    );

    centerColumns.forEach((column, index) => {
        if (appColumnsById.get(column.id)?.kind === 'action') {
            renderedCenterIndexes.add(index);
        }
    });

    if (activeEditKey) {
        const activeDraft = drafts.get(activeEditKey);
        const activeSource = activeDraft?.draft ?? sourceRowsByKey.get(activeEditKey);
        const activeColumnIds = activeCell
            ? [activeCell.columnId]
            : activeRow && activeSource
              ? columns
                    .filter(
                        (column) =>
                            column.kind !== 'action' && column.setValue && column.canEdit?.(activeSource) !== false,
                    )
                    .map((column) => column.id)
              : [];

        centerColumns.forEach((column, index) => {
            if (activeColumnIds.includes(column.id)) {
                renderedCenterIndexes.add(index);
            }
        });
    }

    const centerSegments = useMemo(() => {
        const segments: Array<
            { kind: 'column'; column: (typeof centerColumns)[number] } | { kind: 'spacer'; width: number; span: number }
        > = [];
        let spacerWidth = 0;
        let spacerSpan = 0;

        centerColumns.forEach((column, index) => {
            if (!renderedCenterIndexes.has(index)) {
                spacerWidth += column.getSize();
                spacerSpan++;

                return;
            }

            if (spacerWidth > 0) {
                segments.push({
                    kind: 'spacer',
                    width: spacerWidth,
                    span: spacerSpan,
                });
                spacerWidth = 0;
                spacerSpan = 0;
            }
            segments.push({
                kind: 'column',
                column,
            });
        });

        if (spacerWidth > 0) {
            segments.push({
                kind: 'spacer',
                width: spacerWidth,
                span: spacerSpan,
            });
        }

        return segments;
    }, [
        centerColumns,
        table.state.columnSizing,
        columnVirtualizer
            .getVirtualItems()
            .map((item) => item.index)
            .join(','),
        activeRow,
        activeCell,
        drafts,
        sourceRowsByKey,
        columns,
    ]);
    const renderedColumns = [
        ...startColumns,
        ...centerSegments.flatMap((segment) => (segment.kind === 'column' ? [segment.column] : [])),
        ...endColumns,
    ];
    const renderedColumnSegments: Array<
        { kind: 'column'; column: (typeof visibleColumns)[number] } | { kind: 'spacer'; width: number; span: number }
    > = [
        ...startColumns.map((column) => ({
            kind: 'column' as const,
            column,
        })),
        ...centerSegments,
        ...endColumns.map((column) => ({
            kind: 'column' as const,
            column,
        })),
    ];
    const virtualBodyRows = rowVirtualizationEnabled
        ? rowVirtualizer.getVirtualItems().map((item) => ({
              row: visibleRows[item.index],
              index: item.index,
              start: Math.max(0, item.start - headerHeight),
              size: item.size,
          }))
        : visibleRows.map((row, index) => ({
              row,
              index,
              start: index * rowHeight,
              size: rowHeight,
          }));
    const virtualBodySegments: Array<
        { kind: 'row'; row: (typeof visibleRows)[number]; index: number } | { kind: 'spacer'; height: number }
    > = [];
    let bodyOffset = 0;

    virtualBodyRows.forEach((item) => {
        const gap = Math.max(0, item.start - bodyOffset);

        if (gap > 1) {
            virtualBodySegments.push({
                kind: 'spacer',
                height: gap,
            });
        }
        virtualBodySegments.push({
            kind: 'row',
            row: item.row,
            index: item.index,
        });
        bodyOffset = item.start + item.size;
    });

    const remainingBodyHeight = rowVirtualizationEnabled ? Math.max(0, rowVirtualizer.getTotalSize() - bodyOffset) : 0;

    if (remainingBodyHeight > 1) {
        virtualBodySegments.push({
            kind: 'spacer',
            height: remainingBodyHeight,
        });
    }
    useLayoutEffect(() => {
        const updateHeaderOffsets = () => {
            const heights = table
                .getHeaderGroups()
                .map((_group, index) => headerRowRefs.current.get(index)?.getBoundingClientRect().height || 56);
            const offsets = heights.map((_height, index) =>
                heights.slice(0, index).reduce((total, height) => total + height, 0),
            );

            setHeaderOffsets((current) =>
                current.length === offsets.length && current.every((value, index) => value === offsets[index])
                    ? current
                    : offsets,
            );

            const nextHeaderHeight = heights.reduce((total, height) => total + height, 0);

            setHeaderHeight((current) => (current === nextHeaderHeight ? current : nextHeaderHeight));
        };

        updateHeaderOffsets();

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        const observer = new ResizeObserver(updateHeaderOffsets);

        headerRowRefs.current.forEach((row) => observer.observe(row));

        return () => observer.disconnect();
    }, [table, tableColumns, table.state.columnOrder, table.state.columnVisibility, table.state.columnPinning]);
    useLayoutEffect(() => {
        rowVirtualizer.measure();
        columnVirtualizer.measure();
    }, [
        rowHeight,
        visibleRows.length,
        centerColumns.length,
        table.state.columnSizing,
        grouping,
        collapsedGroups,
        headerHeight,
        rowVirtualizer,
        columnVirtualizer,
    ]);

    const exportContext = useMemo<AppExportContext<TRow>>(
        () => ({
            columns: visibleColumns.flatMap((column) => {
                const appColumn = appColumnsById.get(column.id);

                return appColumn && appColumn.kind !== 'action' && appColumn.exportable !== false ? [appColumn] : [];
            }),
            rows: [
                ...groupedTree,
                ...(grandTotals.length > 0
                    ? [
                          {
                              kind: 'grandTotal' as const,
                              aggregates: grandTotals,
                          },
                      ]
                    : []),
            ] as readonly AppExportRow<TRow>[],
            filters,
            filterLogic,
            grandTotals,
            metadata: exportMetadata,
        }),
        [visibleColumns, appColumnsById, groupedTree, grandTotals, filters, filterLogic, exportMetadata],
    );

    useEffect(() => {
        onExportContextChange?.(exportContext);
    }, [onExportContextChange, exportContext]);

    const allGroupIds = useMemo(() => {
        const ids: string[] = [];
        const visit = (nodes: typeof groupedTree) =>
            nodes.forEach((node) => {
                if (node.kind === 'group') {
                    ids.push(node.id);
                    visit(node.children);
                }
            });

        visit(groupedTree);

        return ids;
    }, [groupedTree]);

    const hasEditPermission = (column: AppColumn<TRow>, row: TRow) =>
        Boolean(editing && column.kind !== 'action' && column.setValue && column.canEdit?.(row) !== false);

    const canEditCell = (column: AppColumn<TRow>, row: TRow) => {
        const key = appEditRowKey(getRowId(row));

        return hasEditPermission(column, row) && !commitTokensRef.current.has(key);
    };

    const activateEditing = (row: TRow) => {
        const rowId = getRowId(row);
        const key = appEditRowKey(rowId);

        setDrafts((current) => {
            if (current.has(key)) {
                return current;
            }

            const original = savedRowsRef.current.get(key)?.value ?? sourceRowsByKey.get(key) ?? row;
            const next = new Map(current);

            next.set(key, {
                rowId,
                original,
                draft: original,
            });

            return next;
        });
    };

    const beginEditing = (row: TRow, column: AppColumn<TRow>) => {
        if (!editing) {
            return;
        }

        const rowId = getRowId(row);
        const key = appEditRowKey(rowId);

        if (
            editing.mode === 'cell' &&
            activeCell?.key === key &&
            activeCell.columnId !== column.id &&
            hasEditPermission(column, row)
        ) {
            const previousColumnId = activeCell.columnId;

            void commitEditing(key, previousColumnId, false).then((saved) => {
                if (saved) {
                    activateEditing(row);
                    setActiveCell({
                        key,
                        columnId: column.id,
                    });
                    setActiveRow(undefined);
                }
            });

            return;
        }

        if (!canEditCell(column, row)) {
            return;
        }
        activateEditing(row);

        if (editing.mode === 'row') {
            setActiveRow({
                key,
                columnId: column.id,
            });
            setActiveCell(undefined);
        } else {
            setActiveCell({
                key,
                columnId: column.id,
            });
            setActiveRow(undefined);
        }
    };

    const restoreCellFocus = (key: string, columnId: string) => {
        globalThis.setTimeout(() => cellRefs.current.get(`${key}:${columnId}`)?.focus(), 0);
    };

    const cancelEditing = (key: string, columnId: string) => {
        if (commitTokensRef.current.has(key)) {
            return;
        }
        setDrafts((current) => {
            const next = new Map(current);

            next.delete(key);

            return next;
        });
        setActiveRow((current) => (current?.key === key ? undefined : current));
        setActiveCell((current) => (current?.key === key ? undefined : current));
        restoreCellFocus(key, columnId);
    };

    const commitEditing = (key: string, columnId: string, restoreFocus = true): Promise<boolean> => {
        if (!editing) {
            return Promise.resolve(false);
        }

        const pending = commitPromisesRef.current.get(key);

        if (pending) {
            return pending;
        }

        const entry = draftsRef.current.get(key);

        if (!entry) {
            return Promise.resolve(false);
        }

        const focusAtSubmit = document.activeElement;
        const isRowCommit = editing.mode === 'row';
        const token = key;

        if (commitTokensRef.current.has(token)) {
            return Promise.resolve(false);
        }
        commitTokensRef.current.add(token);
        setSavingRows((current) => new Set(current).add(key));

        const promise = (async () => {
            try {
                const context = isRowCommit
                    ? {
                          rowId: entry.rowId,
                      }
                    : {
                          rowId: entry.rowId,
                          columnId,
                      };
                const persisted = await editing.onCommit(entry.draft, entry.original, context);
                const saved = {
                    source: entry.original,
                    value: persisted ?? entry.draft,
                };

                savedRowsRef.current = new Map(savedRowsRef.current).set(key, saved);
                setSavedRows((current) => new Map(current).set(key, saved));
                setDrafts((current) => {
                    const next = new Map(current);

                    next.delete(key);

                    return next;
                });
                setActiveRow((current) => (current?.key === key ? undefined : current));
                setActiveCell((current) => (current?.key === key ? undefined : current));

                if (restoreFocus && document.activeElement === focusAtSubmit) {
                    restoreCellFocus(key, columnId);
                }

                return true;
            } catch (error) {
                editing.onError?.(
                    error,
                    isRowCommit
                        ? {
                              rowId: entry.rowId,
                          }
                        : {
                              rowId: entry.rowId,
                              columnId,
                          },
                );

                return false;
            } finally {
                commitTokensRef.current.delete(token);
                commitPromisesRef.current.delete(token);
                setSavingRows((current) => {
                    const next = new Set(current);

                    next.delete(key);

                    return next;
                });
            }
        })();

        commitPromisesRef.current.set(token, promise);

        return promise;
    };

    const updateDraft = (key: string, column: AppColumn<TRow>, value: AppScalarValue) => {
        if (commitTokensRef.current.has(key)) {
            return;
        }
        setDrafts((current) => {
            const entry = current.get(key);

            if (!entry) {
                return current;
            }

            const next = new Map(current);

            next.set(key, {
                ...entry,
                draft: setAppColumnValue(column, entry.draft, value),
            });

            return next;
        });
    };

    const setEditorOpen = (open: boolean) => {
        editorOpenRef.current = open;

        if (!open) {
            suppressPopupBlurRef.current = true;
            globalThis.setTimeout(() => {
                suppressPopupBlurRef.current = false;
            }, 0);
        }
    };

    const handleEditorKeyDown = (event: KeyboardEvent<HTMLDivElement>, key: string, column: AppColumn<TRow>) => {
        if (editorOpenRef.current && (event.key === 'Enter' || event.key === 'Tab' || event.key === 'Escape')) {
            return;
        }

        if (event.key === 'Escape') {
            event.preventDefault();
            cancelEditing(key, column.id);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            void commitEditing(key, column.id);
        } else if (event.key === 'Tab' && editing?.mode === 'row') {
            event.preventDefault();

            const currentTarget = event.currentTarget;
            const editors = Array.from(
                currentTarget
                    .closest('tr')
                    ?.querySelectorAll<HTMLElement>(
                        '[data-app-editor] input:not([type="hidden"]), [data-app-editor] [role="combobox"], [data-app-editor] [role="checkbox"]',
                    ) ?? [],
            );
            const index = editors.findIndex((editor) => editor === document.activeElement);
            const target = editors[index + (event.shiftKey ? -1 : 1)];

            if (target) {
                target.focus();
            } else {
                void commitEditing(key, column.id, false);
            }
        } else if (event.key === 'Tab' && editing?.mode === 'cell') {
            event.preventDefault();

            const direction = event.shiftKey ? -1 : 1;
            const leafRows = flattenedRows.filter(
                (item): item is Extract<typeof item, { kind: 'leaf' }> => item.kind === 'leaf',
            );
            const rowIndex = leafRows.findIndex((item) => appEditRowKey(item.rowId) === key);
            const orderedIds = visibleColumns.map((item) => item.id);
            const start = rowIndex * orderedIds.length + orderedIds.indexOf(column.id);
            let next: { row: TRow; column: AppColumn<TRow> } | undefined;

            for (
                let position = start + direction;
                position >= 0 && position < leafRows.length * orderedIds.length;
                position += direction
            ) {
                const candidateRow = leafRows[Math.floor(position / orderedIds.length)];
                const candidateColumn = appColumnsById.get(orderedIds[position % orderedIds.length]);
                const candidateKey = appEditRowKey(candidateRow.rowId);
                const candidateValue =
                    drafts.get(candidateKey)?.draft ?? savedRows.get(candidateKey)?.value ?? candidateRow.row;

                if (candidateColumn && canEditCell(candidateColumn, candidateValue)) {
                    next = {
                        row: candidateValue,
                        column: candidateColumn,
                    };
                    break;
                }
            }
            void commitEditing(key, column.id, false).then((saved) => {
                if (saved && next) {
                    globalThis.setTimeout(() => beginEditing(next!.row, next!.column), 0);
                }
            });
        }
    };

    const renderEditor = (column: AppColumn<TRow>, row: TRow, key: string) => {
        const entry = drafts.get(key);

        if (!entry) {
            return defaultAppCell(column, row);
        }

        const value = column.accessor(entry.draft);
        const autoFocus =
            editing?.mode === 'row'
                ? activeRow?.key === key && activeRow.columnId === column.id
                : activeCell?.key === key && activeCell.columnId === column.id;
        const editorProps = {
            row: entry.draft,
            value,
            onChange: (next: AppScalarValue) => updateDraft(key, column, next),
            setEditorOpen,
        };

        return (
            <Box
                data-app-editor
                onKeyDown={(event) => handleEditorKeyDown(event, key, column)}
                onBlur={(event: FocusEvent<HTMLDivElement>) => {
                    const target = event.relatedTarget;

                    if (target && event.currentTarget.contains(target as Node)) {
                        return;
                    }

                    if (editorOpenRef.current || suppressPopupBlurRef.current) {
                        return;
                    }

                    if (editing?.mode === 'cell') {
                        void commitEditing(key, column.id, false);
                    } else {
                        const rowElement = event.currentTarget.closest('tr');
                        const nextRow = target instanceof Node ? target.parentElement?.closest('tr') : null;

                        if (rowElement && nextRow !== rowElement) {
                            void commitEditing(key, column.id, false);
                        }
                    }
                }}
            >
                {column.editor ? (
                    column.editor.render({
                        ...editorProps,
                        disabled: savingRows.has(key),
                        autoFocus,
                    })
                ) : (
                    <DefaultAppEditor
                        column={column}
                        row={entry.draft}
                        value={value}
                        onChange={editorProps.onChange}
                        autoFocus={autoFocus}
                        disabled={savingRows.has(key)}
                        setEditorOpen={setEditorOpen}
                        onAccept={() =>
                            globalThis.setTimeout(() => {
                                if (editing?.mode === 'cell') {
                                    void commitEditing(key, column.id, false);
                                }
                            }, 0)
                        }
                    />
                )}
            </Box>
        );
    };

    const renderHeader = (header: Header<typeof appTableFeatures, TRow>) => {
        if (header.isPlaceholder) {
            return null;
        }

        const column = header.column;
        const appColumn = appColumnsById.get(column.id);
        const isGroup = column.columns.length > 0;
        const sortDirection = column.getIsSorted();

        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isGroup ? 'center' : 'flex-start',
                    minWidth: 0,
                    width: '100%',
                    gap: 0.5,
                }}
            >
                {column.getCanSort() && !isGroup ? (
                    <Button
                        size="small"
                        color="inherit"
                        onClick={column.getToggleSortingHandler()}
                        aria-label={`Sort by ${appColumn?.label ?? column.id}${sortDirection ? ` ${sortDirection}` : ''}`}
                        aria-pressed={Boolean(sortDirection)}
                        sx={{
                            minWidth: 0,
                            justifyContent: appColumn?.align === 'right' ? 'flex-end' : 'flex-start',
                            flex: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                        }}
                    >
                        {column.columnDef.header as string}
                        {sortDirection === 'asc' ? ' ▲' : sortDirection === 'desc' ? ' ▼' : ''}
                    </Button>
                ) : (
                    <Box
                        component="span"
                        sx={{
                            fontWeight: 600,
                            px: 1,
                            textAlign: isGroup ? 'center' : 'left',
                        }}
                    >
                        {column.columnDef.header as string}
                    </Box>
                )}
                {columnControls?.menu && !isGroup && appColumn?.kind !== 'action' && (
                    <IconButton
                        className="app-column-menu"
                        size="small"
                        aria-label={`${appColumn?.label ?? column.id} options`}
                        onClick={(event) =>
                            setColumnMenu({
                                columnId: column.id,
                                anchor: event.currentTarget,
                            })
                        }
                    >
                        <SvgIcon fontSize="small" aria-hidden="true">
                            <path d="M12 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zm0 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm0 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4z" />
                        </SvgIcon>
                    </IconButton>
                )}
                {resizeEnabled && column.getCanResize() && !isGroup && (
                    <Box
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize ${appColumn?.label ?? column.id}`}
                        onMouseDown={(event) => {
                            if (appColumn?.flex !== undefined && appColumn.width === undefined) {
                                manualFlexWidths.current.add(column.id);
                            }
                            header.getResizeHandler()(event);
                        }}
                        onTouchStart={(event) => {
                            if (appColumn?.flex !== undefined && appColumn.width === undefined) {
                                manualFlexWidths.current.add(column.id);
                            }
                            header.getResizeHandler()(event);
                        }}
                        sx={{
                            width: 6,
                            alignSelf: 'stretch',
                            cursor: 'col-resize',
                            touchAction: 'none',
                            flexShrink: 0,
                        }}
                    />
                )}
            </Box>
        );
    };

    const visibleColumnCount = visibleColumns.length;
    const hasGroupingControls = groupingAllowed && groupableColumns.length > 0;
    const hasTableToolbar =
        filterControls || columnControls?.visibility || hasGroupingControls || exportOptions !== false;
    const filterPanelId = `${groupIdPrefix}-filters-panel`;
    const groupingPanelId = `${groupIdPrefix}-grouping-panel`;

    return (
        <Paper
            variant="outlined"
            sx={{
                width: '100%',
                overflow: 'hidden',
            }}
        >
            {hasTableToolbar && (
                <Stack
                    role="toolbar"
                    aria-label="Table actions"
                    direction="row"
                    spacing={0.5}
                    useFlexGap
                    flexWrap="wrap"
                    alignItems="center"
                    sx={{
                        px: 0.5,
                        py: 0.5,
                        minHeight: 36,
                        bgcolor: 'grey.200',
                        borderBottom: 1,
                        borderColor: 'grey.400',
                        gap: 0.5,
                    }}
                >
                    {columnControls?.visibility && (
                        <Button
                            ref={columnsButtonRef}
                            size="small"
                            startIcon={<TableIcon name="columns" />}
                            onClick={(event) => setVisibilityMenu(event.currentTarget)}
                        >
                            Columns
                        </Button>
                    )}
                    {filterControls && (
                        <Button
                            ref={filterButtonRef}
                            size="small"
                            startIcon={<TableIcon name="filter" />}
                            aria-haspopup="dialog"
                            aria-controls={filtersAnchor ? filterPanelId : undefined}
                            aria-expanded={Boolean(filtersAnchor)}
                            onClick={(event) => setFiltersAnchor(event.currentTarget)}
                        >
                            {(() => {
                                const activeFilterCount = filters.filter((filter) => filter.scalarType !== 'draft').length;
                                return `Filters${activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}`;
                            })()}
                        </Button>
                    )}
                    {hasGroupingControls && (
                        <Button
                            size="small"
                            startIcon={<TableIcon name="group" />}
                            aria-haspopup="dialog"
                            aria-controls={groupingAnchor ? groupingPanelId : undefined}
                            aria-expanded={Boolean(groupingAnchor)}
                            onClick={(event) => setGroupingAnchor(event.currentTarget)}
                        >
                            Grouping{grouping.length > 0 ? ` (${grouping.length})` : ''}
                        </Button>
                    )}
                    {exportOptions !== false && <AppTableToolbar context={exportContext} />}
                </Stack>
            )}
            {loading && <LinearProgress aria-label="Loading rows" />}
            {filterControls && (
                <Popover
                    open={Boolean(filtersAnchor)}
                    anchorEl={filtersAnchor}
                    onClose={() => setFiltersAnchor(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    slotProps={{
                        paper: {
                            id: filterPanelId,
                            role: 'dialog',
                            'aria-label': 'Filters',
                            sx: {
                                mt: 0.5,
                                width: 'min(664px, calc(100vw - 32px))',
                                maxWidth: 'calc(100vw - 32px)',
                                boxSizing: 'border-box',
                                maxHeight: 'min(70vh, 560px)',
                                overflowX: 'auto',
                                overflowY: 'auto',
                                bgcolor: 'background.paper',
                                boxShadow: 6,
                            },
                        },
                    }}
                >
                    <AppFilterControls
                        columns={columns}
                        rows={rows}
                        filters={filters}
                        logic={filterLogic}
                        updateFilters={(update) =>
                            requestFilterModelChange((current) => ({
                                ...current,
                                filters: update(current.filters),
                            }))
                        }
                        onLogicChange={(next) =>
                            requestFilterModelChange((current) => ({
                                ...current,
                                logic: next,
                            }))
                        }
                    />
                </Popover>
            )}
            {hasGroupingControls && (
                <Popover
                    open={Boolean(groupingAnchor)}
                    anchorEl={groupingAnchor}
                    onClose={() => setGroupingAnchor(null)}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    slotProps={{
                        paper: {
                            id: groupingPanelId,
                            role: 'dialog',
                            'aria-label': 'Grouping',
                            sx: {
                                mt: 0.5,
                                p: 1,
                                minWidth: 300,
                                maxWidth: 'min(520px, calc(100vw - 32px))',
                                maxHeight: 'min(70vh, 560px)',
                                overflow: 'auto',
                                bgcolor: 'background.paper',
                                boxShadow: 6,
                            },
                        },
                    }}
                >
                    <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <FormControl
                                size="small"
                                variant="standard"
                                sx={{
                                    minWidth: 180,
                                }}
                            >
                                <InputLabel id={`${groupIdPrefix}-group-by-label`} shrink>
                                    Group by
                                </InputLabel>
                                <Select
                                    native
                                    variant="standard"
                                    labelId={`${groupIdPrefix}-group-by-label`}
                                    label="Group by"
                                    inputProps={{
                                        'aria-label': 'Group by',
                                    }}
                                    value={groupToAdd}
                                    onChange={(event) => {
                                        const id = event.target.value;

                                        if (id && !grouping.includes(id)) {
                                            setGrouping((current) => [...current, id]);
                                            table.setPageIndex(0);
                                        }
                                        setGroupToAdd('');
                                    }}
                                >
                                    <option value="">Add grouping…</option>
                                    {groupableColumns
                                        .filter((column) => !grouping.includes(column.id))
                                        .map((column) => (
                                            <option key={column.id} value={column.id}>
                                                {column.label}
                                            </option>
                                        ))}
                                </Select>
                            </FormControl>
                            <Button
                                size="small"
                                startIcon={<TableIcon name="expandLess" />}
                                disabled={
                                    allGroupIds.length === 0 || allGroupIds.every((id) => collapsedGroups.has(id))
                                }
                                onClick={() => setCollapsedGroups(new Set(allGroupIds))}
                            >
                                Collapse all
                            </Button>
                            <Button
                                size="small"
                                startIcon={<TableIcon name="expandMore" />}
                                disabled={collapsedGroups.size === 0}
                                onClick={() => setCollapsedGroups(new Set())}
                            >
                                Expand all
                            </Button>
                        </Stack>
                        {grouping.map((id, index) => {
                            const column = groupableColumns.find((item) => item.id === id);

                            if (!column) {
                                return null;
                            }

                            return (
                                <Stack
                                    key={`${id}-${index}`}
                                    direction="row"
                                    spacing={0.25}
                                    alignItems="center"
                                    sx={{
                                        px: 1,
                                        py: 0.25,
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            flex: 1,
                                        }}
                                    >
                                        {column.label}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        aria-label={`Move ${column.label} grouping up`}
                                        disabled={index === 0}
                                        onClick={() =>
                                            setGrouping((current) => {
                                                const next = [...current];

                                                [next[index - 1], next[index]] = [next[index], next[index - 1]];

                                                return next;
                                            })
                                        }
                                    >
                                        <TableIcon name="up" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        aria-label={`Move ${column.label} grouping down`}
                                        disabled={index === grouping.length - 1}
                                        onClick={() =>
                                            setGrouping((current) => {
                                                const next = [...current];

                                                [next[index], next[index + 1]] = [next[index + 1], next[index]];

                                                return next;
                                            })
                                        }
                                    >
                                        <TableIcon name="down" />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        aria-label={`Remove ${column.label} grouping`}
                                        onClick={() => {
                                            setGrouping((current) =>
                                                current.filter((_item, itemIndex) => itemIndex !== index),
                                            );
                                            table.setPageIndex(0);
                                        }}
                                    >
                                        <TableIcon name="close" />
                                    </IconButton>
                                </Stack>
                            );
                        })}
                    </Stack>
                </Popover>
            )}
            <TableContainer
                ref={containerRef}
                sx={{
                    maxHeight: 640,
                    overflow: 'auto',
                }}
            >
                <Table
                    stickyHeader
                    size={density === 'compact' ? 'small' : 'medium'}
                    aria-label="Data table"
                    sx={{
                        minWidth: Math.max(table.getTotalSize(), 360),
                        tableLayout: 'fixed',
                    }}
                >
                    <colgroup>
                        {visibleColumns.map((column) => (
                            <col
                                key={column.id}
                                style={{
                                    width: column.getSize(),
                                }}
                            />
                        ))}
                    </colgroup>
                    <TableHead>
                        {table.getHeaderGroups().map((group, groupIndex) => (
                            <TableRow
                                key={group.id}
                                ref={(element: HTMLTableRowElement | null) => {
                                    if (element) {
                                        headerRowRefs.current.set(groupIndex, element);
                                    } else {
                                        headerRowRefs.current.delete(groupIndex);
                                    }
                                }}
                                sx={{
                                    minHeight: 56,
                                    height: 56,
                                }}
                            >
                                {group.headers.map((header) => {
                                    const appColumn = appColumnsById.get(header.column.id);
                                    const pinnedStyle = pinningStyles(header.column);

                                    return (
                                        <TableCell
                                            key={header.id}
                                            data-column-id={header.column.id}
                                            data-column-menu-open={
                                                columnMenu?.columnId === header.column.id ? 'true' : undefined
                                            }
                                            colSpan={header.colSpan}
                                            align={appColumn?.align ?? 'left'}
                                            aria-sort={
                                                !header.column.columns.length
                                                    ? header.column.getIsSorted() === 'asc'
                                                        ? 'ascending'
                                                        : header.column.getIsSorted() === 'desc'
                                                          ? 'descending'
                                                          : 'none'
                                                    : undefined
                                            }
                                            sx={{
                                                width: header.getSize(),
                                                minWidth: appColumn?.minWidth,
                                                height: 56,
                                                p: 0.5,
                                                backgroundColor: header.column.columns.length
                                                    ? theme.palette.grey[300]
                                                    : theme.palette.grey[200],
                                                '& .app-column-menu': {
                                                    opacity:
                                                        !autoHideMenu || columnMenu?.columnId === header.column.id
                                                            ? 1
                                                            : 0,
                                                    transition: 'opacity 120ms ease',
                                                },
                                                '&:hover .app-column-menu, &:focus-within .app-column-menu, &[data-column-menu-open="true"] .app-column-menu':
                                                    {
                                                        opacity: 1,
                                                    },
                                                '@media (hover: none)': {
                                                    '& .app-column-menu': {
                                                        opacity: 1,
                                                    },
                                                },
                                                borderBottom: 1,
                                                borderColor: 'divider',
                                                top: headerOffsets[groupIndex] ?? groupIndex * 56,
                                                minHeight: 56,
                                                whiteSpace: 'normal',
                                                overflowWrap: 'anywhere',
                                                ...(header.column.columns.length
                                                    ? {
                                                          textAlign: 'center',
                                                      }
                                                    : {}),
                                                ...pinnedStyle,
                                            }}
                                        >
                                            {renderHeader(header)}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHead>
                    <TableBody>
                        {virtualBodySegments.map((bodySegment, bodyIndex) => {
                            if (bodySegment.kind === 'spacer') {
                                return (
                                    <TableRow
                                        key={`virtual-spacer:${bodyIndex}`}
                                        aria-hidden="true"
                                        data-virtual-spacer="true"
                                    >
                                        <TableCell
                                            colSpan={Math.max(visibleColumnCount, 1)}
                                            sx={{
                                                height: bodySegment.height,
                                                p: 0,
                                                border: 0,
                                                lineHeight: 0,
                                            }}
                                        />
                                    </TableRow>
                                );
                            }

                            const { row, index: rowIndex } = bodySegment;

                            if (row.kind === 'group') {
                                const aggregateById = new Map(
                                    row.aggregates.map((aggregate) => [aggregate.columnId, aggregate.value]),
                                );
                                const labelColumn = renderedColumns.find(
                                    (column) => appColumnsById.get(column.id)?.kind !== 'action',
                                );
                                const labelAppColumn = labelColumn ? appColumnsById.get(labelColumn.id) : undefined;
                                const labelColumnIndex = labelColumn
                                    ? visibleColumns.findIndex((column) => column.id === labelColumn.id)
                                    : -1;
                                const labelLeadingWidth =
                                    labelColumnIndex >= 0
                                        ? visibleColumns.slice(labelColumnIndex).reduce(
                                              (state, column, index) => {
                                                  if (state.done) {
                                                      return state;
                                                  }

                                                  const appColumn = appColumnsById.get(column.id);

                                                  if (
                                                      index > 0 &&
                                                      (appColumn?.kind === 'action' || appColumn?.aggregate !== undefined)
                                                  ) {
                                                      return {
                                                          ...state,
                                                          done: true,
                                                      };
                                                  }

                                                  return {
                                                      width: state.width + column.getSize(),
                                                      done: false,
                                                  };
                                              },
                                              {
                                                  width: 0,
                                                  done: false,
                                              },
                                          ).width
                                        : (labelColumn?.getSize() ?? 100);
                                const labelMaxWidth =
                                    labelAppColumn?.aggregate !== undefined
                                        ? (labelColumn?.getSize() ?? 100)
                                        : Math.min(labelLeadingWidth, Math.max(100, containerWidth * 0.9));

                                return (
                                    <TableRow
                                        key={row.id}
                                        data-index={rowIndex}
                                        data-virtual-index={rowIndex}
                                        ref={rowVirtualizationEnabled ? rowVirtualizer.measureElement : undefined}
                                        sx={{
                                            height: rowHeight,
                                            backgroundColor: theme.palette.grey[300],
                                        }}
                                    >
                                        {renderedColumnSegments.map((segment, columnIndex) => {
                                            if (segment.kind === 'spacer') {
                                                return (
                                                    <TableCell
                                                        key={`group-spacer:${row.id}:${columnIndex}`}
                                                        colSpan={segment.span}
                                                        aria-hidden="true"
                                                        sx={{
                                                            width: segment.width,
                                                            minWidth: segment.width,
                                                            maxWidth: segment.width,
                                                            p: 0,
                                                            border: 0,
                                                        }}
                                                    />
                                                );
                                            }

                                            const column = segment.column;
                                            const appColumn = appColumnsById.get(column.id);
                                            const aggregateValue = aggregateById.get(column.id);

                                            return (
                                                <TableCell
                                                    key={column.id}
                                                    data-column-id={column.id}
                                                    align={appColumn?.align ?? 'left'}
                                                    sx={{
                                                        width: column.getSize(),
                                                        px: 1,
                                                        py: density === 'compact' ? 0.25 : 0.75,
                                                        fontWeight: 600,
                                                        overflow: column.id === labelColumn?.id ? 'visible' : undefined,
                                                        position:
                                                            column.id === labelColumn?.id ? 'relative' : undefined,
                                                        zIndex: column.id === labelColumn?.id ? 2 : undefined,
                                                        ...pinningStyles(column),
                                                    }}
                                                >
                                                    {column.id === labelColumn?.id ? (
                                                        <Button
                                                            size="small"
                                                            color="inherit"
                                                            aria-expanded={!collapsedGroups.has(row.id)}
                                                            aria-label={`${collapsedGroups.has(row.id) ? 'Expand' : 'Collapse'} ${appColumnsById.get(row.columnId)?.label ?? 'group'} ${row.label} (${row.leafCount})`}
                                                            onClick={() =>
                                                                setCollapsedGroups((current) => {
                                                                    const next = new Set(current);

                                                                    if (next.has(row.id)) {
                                                                        next.delete(row.id);
                                                                    } else {
                                                                        next.add(row.id);
                                                                    }

                                                                    return next;
                                                                })
                                                            }
                                                            sx={{
                                                                textTransform: 'none',
                                                                minWidth: 0,
                                                                maxWidth: labelMaxWidth,
                                                                width: 'max-content',
                                                                justifyContent: 'flex-start',
                                                                whiteSpace:
                                                                    labelAppColumn?.aggregate !== undefined
                                                                        ? 'normal'
                                                                        : 'nowrap',
                                                                overflow: 'visible',
                                                                display: 'block',
                                                                pl: `${row.depth * 1.5}rem`,
                                                            }}
                                                        >
                                                            <TableIcon
                                                                name={
                                                                    collapsedGroups.has(row.id)
                                                                        ? 'chevronRight'
                                                                        : 'chevronDown'
                                                                }
                                                            />{' '}
                                                            {appColumnsById.get(row.columnId)?.label}: {row.label} (
                                                            {row.leafCount})
                                                        </Button>
                                                    ) : null}
                                                    {column.id === labelColumn?.id && aggregateValue !== undefined && (
                                                        <Typography
                                                            component="div"
                                                            variant="body2"
                                                            sx={{
                                                                width: '100%',
                                                                textAlign: appColumn?.align ?? 'left',
                                                            }}
                                                        >
                                                            {appColumn?.aggregateFormatter?.(aggregateValue) ??
                                                                String(aggregateValue)}
                                                        </Typography>
                                                    )}
                                                    {column.id !== labelColumn?.id && aggregateValue !== undefined
                                                        ? (appColumn?.aggregateFormatter?.(aggregateValue) ??
                                                          String(aggregateValue))
                                                        : null}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            }

                            const leaf = row;
                            const rowKey = appEditRowKey(leaf.rowId);
                            const source = drafts.get(rowKey)?.draft ?? savedRows.get(rowKey)?.value ?? leaf.row;

                            return (
                                <TableRow
                                    key={`leaf:${typeof leaf.rowId}:${String(leaf.rowId)}`}
                                    data-index={rowIndex}
                                    data-virtual-index={rowIndex}
                                    ref={rowVirtualizationEnabled ? rowVirtualizer.measureElement : undefined}
                                    hover
                                    sx={{
                                        height: rowHeight,
                                        backgroundColor:
                                            rowIndex % 2 === 0 ? theme.palette.common.white : theme.palette.grey[200],
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                        },
                                        '&:focus-within': {
                                            outline: `2px solid ${theme.palette.primary.main}`,
                                            outlineOffset: -2,
                                        },
                                    }}
                                >
                                    {renderedColumnSegments.map((segment, columnIndex) => {
                                        if (segment.kind === 'spacer') {
                                            return (
                                                <TableCell
                                                    key={`leaf-spacer:${rowKey}:${columnIndex}`}
                                                    colSpan={segment.span}
                                                    aria-hidden="true"
                                                    sx={{
                                                        width: segment.width,
                                                        minWidth: segment.width,
                                                        maxWidth: segment.width,
                                                        p: 0,
                                                        border: 0,
                                                    }}
                                                />
                                            );
                                        }

                                        const column = segment.column;
                                        const appColumn = appColumnsById.get(column.id);
                                        const editable = appColumn ? hasEditPermission(appColumn, source) : false;
                                        const editingThisCell = Boolean(
                                            editing &&
                                            appColumn &&
                                            editable &&
                                            (editing.mode === 'row'
                                                ? activeRow?.key === rowKey
                                                : activeCell?.key === rowKey && activeCell.columnId === column.id),
                                        );
                                        const value = appColumn
                                            ? editingThisCell
                                                ? renderEditor(appColumn, source, rowKey)
                                                : defaultAppCell(appColumn, source)
                                            : '';
                                        const pinnedStyle = pinningStyles(column);

                                        return (
                                            <TableCell
                                                key={`leaf:${typeof leaf.rowId}:${String(leaf.rowId)}:${column.id}`}
                                                data-column-id={column.id}
                                                ref={(element: HTMLTableCellElement | null) => {
                                                    const refKey = `${rowKey}:${column.id}`;

                                                    if (element) {
                                                        cellRefs.current.set(refKey, element);
                                                    } else {
                                                        cellRefs.current.delete(refKey);
                                                    }
                                                }}
                                                tabIndex={editable ? 0 : undefined}
                                                onDoubleClick={() => {
                                                    if (appColumn) {
                                                        beginEditing(source, appColumn);
                                                    }
                                                }}
                                                onKeyDown={(event) => {
                                                    if (
                                                        event.key === 'Enter' &&
                                                        editable &&
                                                        !editingThisCell &&
                                                        appColumn
                                                    ) {
                                                        event.preventDefault();
                                                        beginEditing(source, appColumn);
                                                    }
                                                }}
                                                align={appColumn?.align ?? 'left'}
                                                sx={{
                                                    width: column.getSize(),
                                                    minWidth: appColumn?.minWidth,
                                                    py: density === 'compact' ? 0.25 : 0.75,
                                                    px: 1,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                    backgroundColor:
                                                        pinnedStyle.position === 'sticky'
                                                            ? rowIndex % 2 === 0
                                                                ? theme.palette.common.white
                                                                : theme.palette.grey[200]
                                                            : undefined,
                                                    ...pinnedStyle,
                                                }}
                                            >
                                                {value}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                        {visibleRows.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={Math.max(visibleColumnCount, 1)}
                                    align="center"
                                    sx={{
                                        py: 4,
                                    }}
                                >
                                    {loading ? <CircularProgress size={24} aria-label="Loading rows" /> : 'No rows'}
                                </TableCell>
                            </TableRow>
                        )}
                        {grandTotals.length > 0 && (
                            <TableRow
                                sx={{
                                    position: 'sticky',
                                    bottom: 0,
                                    zIndex: 3,
                                    height: rowHeight,
                                    backgroundColor: theme.palette.grey[200],
                                    fontWeight: 700,
                                }}
                            >
                                {renderedColumnSegments.map((segment, columnIndex) => {
                                    if (segment.kind === 'spacer') {
                                        return (
                                            <TableCell
                                                key={`total-spacer:${columnIndex}`}
                                                colSpan={segment.span}
                                                aria-hidden="true"
                                                sx={{
                                                    width: segment.width,
                                                    minWidth: segment.width,
                                                    maxWidth: segment.width,
                                                    p: 0,
                                                    border: 0,
                                                }}
                                            />
                                        );
                                    }

                                    const column = segment.column;
                                    const appColumn = appColumnsById.get(column.id);
                                    const value = grandTotals.find(
                                        (aggregate) => aggregate.columnId === column.id,
                                    )?.value;

                                    return (
                                        <TableCell
                                            key={`grand:${column.id}`}
                                            data-column-id={column.id}
                                            align={appColumn?.align ?? 'left'}
                                            sx={{
                                                position: 'sticky',
                                                bottom: 0,
                                                zIndex: 4,
                                                px: 1,
                                                py: density === 'compact' ? 0.25 : 0.75,
                                                backgroundColor: theme.palette.grey[200],
                                                fontWeight: 700,
                                                ...pinningStyles(column),
                                            }}
                                        >
                                            {column.id === renderedColumns[0]?.id ? (
                                                <Stack
                                                    spacing={0}
                                                    sx={{
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            width: '100%',
                                                        }}
                                                    >
                                                        Grand total
                                                    </Box>
                                                    {value !== undefined && (
                                                        <Box
                                                            component="span"
                                                            sx={{
                                                                width: '100%',
                                                                textAlign: appColumn?.align ?? 'left',
                                                            }}
                                                        >
                                                            {appColumn?.aggregateFormatter?.(value) ?? String(value)}
                                                        </Box>
                                                    )}
                                                </Stack>
                                            ) : value !== undefined ? (
                                                (appColumn?.aggregateFormatter?.(value) ?? String(value))
                                            ) : null}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {pagination && (
                <TablePagination
                    component="div"
                    count={flattenedRows.length}
                    page={table.state.pagination.pageIndex}
                    rowsPerPage={table.state.pagination.pageSize}
                    rowsPerPageOptions={[...new Set([25, 50, 100, 250, table.state.pagination.pageSize])].sort(
                        (left, right) => left - right,
                    )}
                    onPageChange={(_event, page) => table.setPageIndex(page)}
                    onRowsPerPageChange={(event) => {
                        table.setPageSize(Number(event.target.value));
                        table.setPageIndex(0);
                    }}
                />
            )}
            {columnMenuProps && columnMenuAppColumn && (
                <AppColumnMenu
                    table={table}
                    columnId={columnMenuProps.columnId}
                    anchorEl={columnMenuProps.anchor}
                    appColumn={columnMenuAppColumn}
                    autoHideMenu={autoHideMenu}
                    onAutoHideMenuChange={setAutoHideMenu}
                    groupingAllowed={groupingAllowed}
                    isGrouped={grouping.includes(columnMenuProps.columnId)}
                    onGrouping={() => {
                        const id = columnMenuProps.columnId;

                        setGrouping((current) =>
                            current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
                        );
                        table.setPageIndex(0);
                    }}
                    onFilter={
                        filterControls
                            ? () => {
                                  const id = columnMenuProps.columnId;
                                  const appColumn = appColumnsById.get(id);

                                  if (appColumn) {
                                      requestFilterModelChange((current) =>
                                          current.filters.some((filter) => filter.columnId === id)
                                              ? current
                                              : {
                                                    ...current,
                                                    filters: [...current.filters, createEmptyAppFilter(appColumn)],
                                                },
                                      );
                                  }
                                  setFiltersAnchor(filterButtonRef.current);
                              }
                            : undefined
                    }
                    allowVisibility={Boolean(columnControls?.visibility)}
                    onManageColumns={
                        columnControls?.visibility ? () => setVisibilityMenu(columnsButtonRef.current) : undefined
                    }
                    onClose={() => setColumnMenu(undefined)}
                />
            )}
            <Menu anchorEl={visibilityMenu} open={Boolean(visibilityMenu)} onClose={() => setVisibilityMenu(null)}>
                {table.getAllLeafColumns().map((column) => (
                    <MenuItem key={column.id} dense disabled={!column.getCanHide()}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={column.getIsVisible()}
                                    size="small"
                                    onChange={(event) => column.toggleVisibility(event.target.checked)}
                                />
                            }
                            label={appColumnsById.get(column.id)?.label ?? column.id}
                            onClick={(event) => event.stopPropagation()}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </Paper>
    );
}

export default AppTable;
